import type { GrassSimulationBaseline } from './atlas-data'

export type SimulationWater = 'low' | 'right' | 'too-much'
export type SimulationMowing = 'too-short' | 'healthy' | 'let-grow'
export type SimulationNutrients = 'low' | 'balanced'

export interface GrassSimulationInput {
  day: number
  water: SimulationWater
  mowing: SimulationMowing
  nutrients: SimulationNutrients
}

export interface GrassSimulationVisualState {
  bladeScale: number
  coverageScale: number
  rootScale: number
  colorHealth: number
  seedheadVisibility: number
  freshCut: number
  cutHeightRatio: number
  extraBladeVisibility: number
  nutrientChlorosis: number
  droughtStress: number
  heightInches: number
  plantColorScore: number
  rootEnergyScore: number
  stage: 'Seed' | 'Sprouting' | 'Filling in' | 'Established' | 'Overgrown'
  visualCue: 'Healthy green' | 'Freshly cut' | 'Thicker grass' | 'Yellowing' | 'Drying' | 'Establishing'
  message: string
}

export interface GrassSimulationPreset {
  id: 'healthy' | 'too-short' | 'overgrown' | 'dry' | 'low-nutrients'
  label: string
  water: SimulationWater
  mowing: SimulationMowing
  nutrients: SimulationNutrients
  previewDay: number
}

export const GRASS_SIMULATION_PRESETS: GrassSimulationPreset[] = [
  { id: 'healthy', label: 'Healthy growth', water: 'right', mowing: 'healthy', nutrients: 'balanced', previewDay: 90 },
  { id: 'too-short', label: 'Too short', water: 'right', mowing: 'too-short', nutrients: 'balanced', previewDay: 36 },
  { id: 'overgrown', label: 'Overgrown', water: 'right', mowing: 'let-grow', nutrients: 'balanced', previewDay: 90 },
  { id: 'dry', label: 'Dry', water: 'low', mowing: 'healthy', nutrients: 'balanced', previewDay: 90 },
  { id: 'low-nutrients', label: 'Not enough food', water: 'right', mowing: 'healthy', nutrients: 'low', previewDay: 90 },
]

export const SIMULATION_SAMPLE_DAYS = [0, 7, 14, 21, 35, 50, 70, 90] as const

// Day the Grow it lab opens on: the first sample day where every species shows a plant
// tall enough to read as growing grass. Day 0 is still reachable with the slider.
export const SIMULATION_OPENING_DAY = 35

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
const smoothstep = (edge0: number, edge1: number, value: number) => {
  const progress = clamp((value - edge0) / Math.max(0.0001, edge1 - edge0), 0, 1)
  return progress * progress * (3 - 2 * progress)
}

function responseMessage(input: GrassSimulationInput, stage: GrassSimulationVisualState['stage']) {
  if (stage === 'Seed') return 'The seed is still waiting below the soil. Keep the seedbed evenly moist.'
  if (stage === 'Sprouting') return 'New shoots are emerging. Young roots and blades are still small.'
  if (input.water === 'low') return 'Too little water gradually dulls the leaves and slows top and root growth.'
  if (input.water === 'too-much') return 'Too much water makes the grass soft and gives the roots less energy.'
  if (input.nutrients === 'low') return 'Not enough food makes the leaves shorter, thinner, and less green.'
  if (input.mowing === 'too-short' && input.day >= 35) return 'Cutting this low takes off most of each leaf, so there is less green leaf left to feed the roots.'
  if (input.mowing === 'let-grow' && input.day >= 35) return 'Uncut growth makes the canopy taller and fuller. Mature seedheads may rise above grasses that produce a visible one.'
  return stage === 'Filling in'
    ? 'Blades and roots are filling the open space as the young lawn establishes.'
    : 'Enough water, a healthy cut, and balanced nutrients support steady green growth.'
}

export function getGrassSimulationState(
  input: GrassSimulationInput,
  baseline: GrassSimulationBaseline,
): GrassSimulationVisualState {
  const day = clamp(Number.isFinite(input.day) ? input.day : 0, 0, 90)
  const establishment = clamp((baseline.establishmentDays.min + baseline.establishmentDays.max) / 2, 5, 60)
  const growth = smoothstep(0, establishment, day)
  const sprout = smoothstep(1.5, Math.max(5, baseline.establishmentDays.min), day)
  const established = smoothstep(establishment * 0.72, establishment * 1.2, day)
  const afterCut = smoothstep(34, 36, day)

  let matureBlade = 0.78
  let coverageFactor = 1
  let rootFactor = 1
  let colorHealth = 1
  let seedheadVisibility = 0
  let freshCut = 0
  let cutHeightRatio = 1
  let extraBladeVisibility = 0
  let nutrientChlorosis = 0
  let droughtStress = 0

  if (input.mowing === 'too-short') {
    freshCut = afterCut
    cutHeightRatio = 1 - afterCut * 0.6
    matureBlade *= 1 - afterCut * 0.1
    rootFactor *= 1 - afterCut * 0.3
    colorHealth -= afterCut * 0.06
  } else if (input.mowing === 'let-grow') {
    const overgrowth = smoothstep(35, 72, day)
    matureBlade = 0.78 + 0.44 * overgrowth
    coverageFactor *= 1 + overgrowth * 0.06
    extraBladeVisibility = overgrowth
    seedheadVisibility = established * smoothstep(48, 75, day)
  }

  const stressAge = sprout * smoothstep(Math.max(5, baseline.establishmentDays.min * 0.6), 70, day)
  if (input.water === 'low') {
    droughtStress = 0.88 * stressAge
    colorHealth -= 0.48 * droughtStress
    coverageFactor *= 1 - 0.25 * stressAge
    rootFactor *= 1 - 0.36 * stressAge
    matureBlade *= 1 - 0.28 * stressAge
  } else if (input.water === 'too-much') {
    const excess = established * smoothstep(establishment * 0.75, 70, day)
    colorHealth -= 0.18 * excess
    coverageFactor *= 1 - 0.1 * excess
    rootFactor *= 1 - 0.2 * excess
  }

  if (input.nutrients === 'low') {
    const hunger = sprout * smoothstep(Math.max(8, baseline.establishmentDays.min), 70, day)
    nutrientChlorosis = 0.9 * hunger
    colorHealth -= 0.4 * nutrientChlorosis
    coverageFactor *= 1 - 0.2 * hunger
    rootFactor *= 1 - 0.26 * hunger
    matureBlade *= 1 - 0.22 * hunger
  }

  const bladeScale = clamp(0.08 + growth * (matureBlade - 0.08), 0.08, 1.28)
  const coverageScale = clamp(0.58 + growth * (coverageFactor - 0.58), 0.58, 1.08)
  const rootScale = clamp(0.45 + growth * (rootFactor - 0.45), 0.45, 1.08)
  colorHealth = clamp(0.42 + sprout * (colorHealth - 0.42), 0, 1)
  seedheadVisibility = clamp(seedheadVisibility, 0, 1)
  freshCut = clamp(freshCut, 0, 1)
  cutHeightRatio = clamp(cutHeightRatio, 0.38, 1)
  extraBladeVisibility = clamp(extraBladeVisibility, 0, 1)
  nutrientChlorosis = clamp(nutrientChlorosis, 0, 1)
  droughtStress = clamp(droughtStress, 0, 1)
  const optimalHeight = (baseline.mowingInches.optimumMin + baseline.mowingInches.optimumMax) / 2
  const heightInches = clamp(optimalHeight * (bladeScale / 0.78) * cutHeightRatio, 0.15, Math.max(6, baseline.mowingInches.optimumMax * 1.7))
  const plantColorScore = Math.round(colorHealth * 100)
  const rootEnergyScore = Math.round(clamp(rootScale, 0, 1) * 100)
  const stage: GrassSimulationVisualState['stage'] = day < 2
    ? 'Seed'
    : sprout < 0.72
      ? 'Sprouting'
      : established < 0.68
        ? 'Filling in'
        : input.mowing === 'let-grow' && bladeScale > 1.02
          ? 'Overgrown'
          : 'Established'
  const visualCue: GrassSimulationVisualState['visualCue'] = day < 2 || sprout < 0.72
    ? 'Establishing'
    : nutrientChlorosis >= 0.15
      ? 'Yellowing'
      : droughtStress >= 0.15
        ? 'Drying'
        : freshCut >= 0.25
          ? 'Freshly cut'
          : extraBladeVisibility >= 0.35
            ? 'Thicker grass'
            : 'Healthy green'
  const result = {
    bladeScale,
    coverageScale,
    rootScale,
    colorHealth,
    seedheadVisibility,
    freshCut,
    cutHeightRatio,
    extraBladeVisibility,
    nutrientChlorosis,
    droughtStress,
    heightInches,
    plantColorScore,
    rootEnergyScore,
    stage,
    visualCue,
    message: '',
  }
  result.message = responseMessage({ ...input, day }, stage)
  return result
}

export function getGrassSimulationSamples(
  input: Omit<GrassSimulationInput, 'day'>,
  baseline: GrassSimulationBaseline,
) {
  return SIMULATION_SAMPLE_DAYS.map((day) => ({
    day,
    state: getGrassSimulationState({ ...input, day }, baseline),
  }))
}

export function getMatureHealthyState(baseline: GrassSimulationBaseline): GrassSimulationVisualState {
  return {
    ...getGrassSimulationState({ day: 90, water: 'right', mowing: 'healthy', nutrients: 'balanced' }, baseline),
    bladeScale: 1,
    coverageScale: 1,
    rootScale: 1,
    colorHealth: 1,
    seedheadVisibility: 1,
    freshCut: 0,
    cutHeightRatio: 1,
    extraBladeVisibility: 0,
    nutrientChlorosis: 0,
    droughtStress: 0,
    visualCue: 'Healthy green',
  }
}
